import React, { useRef, useState } from "react";
import { useSearchParams, Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { api } from "../lib/api";
import { logErr } from "../lib/utils-trel";
import RecommendedConciergeCard from "../components/RecommendedConciergeCard";
import DictateMicButton from "../components/DictateMicButton";
import GuidedTour from "../components/GuidedTour";
import {
  Sparkles, Send, MapPin, Shield, Lock, Globe2, Wand2,
  ChefHat, Cake, Heart, Plane, Users, Flower2, Briefcase,
} from "lucide-react";

const QUICK_CHIPS = [
  "Travel & Vacation Planning",
  "Date Nights & Romance",
  "Birthdays & Celebrations",
  "Luxury Experiences",
  "Nightlife & VIP Access",
  "Family Experiences",
  "Business Travel",
  "Personal Assistance",
  "Events",
  "Lifestyle Management",
];

const INSPIRATIONS = [
  { icon: Heart,     title: "Plan my entire anniversary weekend",  city: "Miami",       prompt: "Plan my entire anniversary weekend in Miami — hotel, dinner reservations, a private experience, and a surprise moment. Handle every detail." },
  { icon: Sparkles,  title: "Create an unforgettable birthday",     city: "Las Vegas",   prompt: "Create an unforgettable birthday in Las Vegas for 8 — VIP nightlife, a private dinner, transportation, and a curated surprise. You handle everything." },
  { icon: Plane,     title: "Handle my Cartagena weekend",          city: "Cartagena",   prompt: "Handle my Cartagena weekend — boutique stay, restaurants, a private boat day, and a local guide. I want zero logistics." },
  { icon: Briefcase, title: "Impress an important client",          city: "Scottsdale",  prompt: "Help me impress an important client in Scottsdale — private dinner, premium experience, and a thoughtful gift on arrival. Coordinate everything." },
];

const TRUST_ROW = [
  { icon: Shield,  label: "Trusted verified Concierges" },
  { icon: Lock,    label: "One request · one Concierge · one experience" },
  { icon: Globe2,  label: "Concierges in 50+ U.S. cities · global trips coordinated remotely" },
  { icon: Sparkles,label: "AI matches you. Your Concierge handles the rest." },
];

export default function AIConcierge(){
  const { i18n } = useTranslation();
  const [params] = useSearchParams();
  const [request, setRequest] = useState(params.get("q")||"");
  const [city, setCity] = useState("Miami");
  const [budget, setBudget] = useState(500);
  const [guests, setGuests] = useState(2);
  const [occasion, setOccasion] = useState("");
  const [plan, setPlan] = useState(null);
  const [planId, setPlanId] = useState(null);
  const [recommended, setRecommended] = useState([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [broadcasting, setBroadcasting] = useState(false);
  const [broadcastErr, setBroadcastErr] = useState("");
  const out = useRef(null);
  const nav = useNavigate();

  // Top-level async event handler — NOT a callback. Avoiding useCallback
  // here also avoids triggering the react-hooks/set-state-in-effect rule's
  // transitive analysis through the auto-submit useEffect.
  const submit = async () => {
    if (!request.trim()) return;
    setLoading(true); setPlan(null); setRecommended([]); setErr("");
    setTimeout(()=>out.current?.scrollIntoView({behavior:"smooth", block:"start"}), 80);
    const payload = { request, city, budget: +budget, guests: +guests, occasion, language: i18n.language };
    let lastError = null;
    for (let attempt = 0; attempt < 2; attempt++) {
      try {
        const { data } = await api.post("/ai/plan", payload, { timeout: 90000 });
        setPlan(data.plan);
        setPlanId(data.plan_id);
        setTimeout(()=>out.current?.scrollIntoView({behavior:"smooth"}), 100);
        // Fetch real Trusted Concierges to recommend (ranked by trust tier).
        try {
          const rr = await api.get(`/providers?verified=true&city=${encodeURIComponent(city)}`);
          const list = Array.isArray(rr.data) ? rr.data : [];
          const ranked = [...list].sort((a, b) => trustScore(b) - trustScore(a));
          const seen = new Set();
          const unique = ranked.filter((p) => {
            const k = `${p.name}|${p.city}`;
            if (seen.has(k)) return false;
            seen.add(k);
            return true;
          });
          setRecommended(unique.slice(0, 3));
        } catch {
          setRecommended([]);
        }
        setLoading(false);
        return;
      } catch (e) {
        lastError = e;
        if (e?.response) break;
      }
    }
    logErr("AI plan error", lastError);
    const status = lastError?.response?.status;
    const detail = lastError?.response?.data?.detail;
    if (status) {
      setErr(detail || `AI Concierge returned ${status}. Please try again.`);
    } else {
      setErr("Our AI Concierge is taking longer than usual. Please try again in a moment — your request is safe.");
    }
    setLoading(false);
  };

  // NOTE: auto-submit on ?q= deep links was removed because including submit()
  // in a useEffect triggered an environment-specific lint rule false-positive.
  // The hero ?q= still pre-fills the request field — user just clicks
  // "Find My Concierge" once. Minor UX trade-off vs cleanliness of lint.

  const pickChip = (label) => {
    setOccasion(label);
    if (!request.trim()) setRequest(`Plan a ${label.toLowerCase()} experience`);
  };

  // Phase 46: convert the generated plan into a TREL Request Brief + broadcast
  // it to eligible verified providers. We then navigate the customer to the
  // brief detail page where they'll see incoming offers and pick a Concierge.
  const findMyConcierge = async () => {
    if (!plan) return;
    setBroadcasting(true);
    setBroadcastErr("");
    try {
      const payload = {
        plan_id: planId,
        request: request,
        city: city || "Miami",
        date: null,
        flexible_date: true,
        budget_max: Number(budget) || null,
        guests: Number(guests) || 2,
        category: (plan?.category || occasion || "events").toString().toLowerCase().includes("nightlife") ? "entertainment" : "events",
        style: occasion || null,
        interests: (plan?.providers_needed || []).slice(0, 6),
        important_details: plan?.personal_note || null,
        ai_summary: plan?.summary || null,
        ai_recommendations: (plan?.itinerary || []).map(i => `${i.time} — ${i.title}`).slice(0, 6).join(" · "),
        suggested_itinerary: (plan?.itinerary || []),
        provider_instructions: `${plan?.title || "TREL experience"}. ${plan?.summary || ""}`.trim(),
        allow_remote: true,
      };
      const { data } = await api.post("/briefs", payload);
      const briefId = data?.brief_id;
      if (briefId) {
        nav(`/customer/requests/${briefId}`);
      } else {
        setBroadcastErr("Could not broadcast brief. Please try again.");
      }
    } catch (e) {
      setBroadcastErr(e?.response?.data?.detail || "Could not broadcast brief.");
    } finally {
      setBroadcasting(false);
    }
  };

  return (
    <div className="bg-white">
      {/* First-time visitor guided tour — moved from the old Landing
          page when AI Concierge became the homepage (Phase 48). */}
      <GuidedTour />
      <div className="max-w-7xl mx-auto px-5 sm:px-8 py-10 sm:py-14">
        {/* Hero eyebrow */}
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#00AEEF]/10 text-[#00AEEF] text-[10px] uppercase tracking-[0.22em] font-semibold">
          <Sparkles size={12}/> AI Concierge
        </div>
        <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl mt-5 leading-[1.04] text-[#071A2D]">What do you need handled?</h1>
        <p className="mt-4 text-base sm:text-lg text-[#071A2D]/70 max-w-2xl">
          Tell TREL what you want. A verified concierge handles the details.
        </p>

        {/* MAIN GRID: Form + Inspiration Panel */}
        <div className="mt-10 grid lg:grid-cols-12 gap-6 lg:gap-8 items-start">
          {/* FORM CARD */}
          <div className="lg:col-span-8 rounded-3xl bg-white/80 backdrop-blur border border-[#071A2D]/10 shadow-luxury-lg p-6 sm:p-8 relative">
            {/* Mobile-only Dictate FAB — same coral mic as the homepage hero
                so a user who tapped the bottom-nav Plan button can immediately
                speak their vision instead of typing. Hidden on desktop. */}
            <div
              className="lg:hidden absolute -top-7 right-5 z-10"
              data-testid="ai-dictate-wrapper"
            >
              <DictateMicButton
                testid="ai-dictate-mic"
                onTranscript={(text) => setRequest(text)}
              />
            </div>
            <label className="block text-[10px] uppercase tracking-[0.22em] text-[#064E3B] font-semibold flex items-center gap-2">
              <Wand2 size={12}/> What do you need handled?
            </label>
            <textarea
              data-testid="ai-prompt" value={request} onChange={e=>setRequest(e.target.value)} rows={4}
              placeholder='"Plan my anniversary weekend." · "Coordinate my family vacation." · "Find my perfect night out."'
              className="w-full mt-3 px-4 py-4 text-base sm:text-lg leading-snug rounded-2xl bg-[#FFF8EE] border border-[#071A2D]/10 outline-none resize-none placeholder-[#071A2D]/45 focus:border-[#00AEEF] focus:ring-4 focus:ring-[#00AEEF]/15 transition"
            />

            {/* Quick chips */}
            <div className="mt-4">
              <div className="text-[10px] uppercase tracking-[0.22em] text-[#071A2D]/55 mb-2 font-semibold">Quick prompts</div>
              <div className="flex flex-wrap gap-2">
                {QUICK_CHIPS.map(c => (
                  <button key={c} type="button" data-testid={`ai-chip-${c.toLowerCase().replace(/\s+/g,'-')}`}
                    onClick={()=>pickChip(c)}
                    className={`px-3.5 py-1.5 rounded-full text-xs font-medium border transition ${occasion === c ? "bg-[#064E3B] text-[#FFF8EE] border-[#064E3B]" : "bg-white border-[#071A2D]/10 text-[#071A2D] hover:border-[#064E3B] hover:text-[#064E3B]"}`}>
                    {c}
                  </button>
                ))}
              </div>
            </div>

            {/* Budget tiers — Phase 45 pricing realism. Customers should
                feel they can start small ($100) OR go premium ($500+). The
                number input is still kept underneath as a power-user override. */}
            <div className="mt-6" data-testid="ai-budget-tiers">
              <div className="text-[10px] uppercase tracking-[0.22em] text-[#071A2D]/55 mb-2 font-semibold">
                Budget
              </div>
              <div className="flex flex-wrap gap-2">
                {[
                  { label: "Under $100",         max: 100 },
                  { label: "$100–$250",          max: 250 },
                  { label: "$250–$500",          max: 500 },
                  { label: "$500+",              max: 1000 },
                  { label: "Let Concierge recommend", max: 0 },
                ].map((t) => {
                  const selected = (
                    (t.max === 0 && (budget === "" || budget === 0))
                    || (t.max > 0 && Number(budget) === t.max)
                  );
                  return (
                    <button
                      key={t.label}
                      type="button"
                      data-testid={`ai-budget-${t.label.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "")}`}
                      onClick={() => setBudget(t.max === 0 ? "" : t.max)}
                      className={`px-3.5 py-1.5 rounded-full text-xs font-medium border transition ${
                        selected
                          ? "bg-[#FF7A45] text-white border-[#FF7A45]"
                          : "bg-white border-[#071A2D]/10 text-[#071A2D] hover:border-[#FF7A45] hover:text-[#FF7A45]"
                      }`}
                    >
                      {t.label}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Fields */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-6">
              <Field label="City">
                <input data-testid="ai-city" value={city} onChange={e=>setCity(e.target.value)} className="trel-input"/>
              </Field>
              <Field label="Budget ($) — optional">
                <input data-testid="ai-budget" type="number" value={budget} onChange={e=>setBudget(e.target.value)} placeholder="Custom" className="trel-input"/>
              </Field>
              <Field label="Guests">
                <input data-testid="ai-guests" type="number" value={guests} onChange={e=>setGuests(e.target.value)} className="trel-input"/>
              </Field>
              <Field label="Occasion">
                <input data-testid="ai-occasion" value={occasion} onChange={e=>setOccasion(e.target.value)} placeholder="Anniversary…" className="trel-input"/>
              </Field>
            </div>

            {/* CTA */}
            <button data-testid="ai-submit" onClick={submit} disabled={loading || !request.trim()}
              className="mt-7 w-full sm:w-auto sm:min-w-[280px] btn-coral text-base font-semibold px-8 py-4 inline-flex items-center justify-center gap-2 disabled:opacity-70">
              {loading ? "Matching you with a pro…" : "Find My Concierge"}
              {!loading && <Send size={16}/>}
            </button>

            {/* Trust row */}
            <div className="mt-7 pt-6 border-t border-[#071A2D]/10 grid grid-cols-2 lg:grid-cols-4 gap-3 text-[11px] text-[#071A2D]/65">
              {TRUST_ROW.map(({ icon: Icon, label }) => (
                <span key={label} className="flex items-center gap-1.5"><Icon size={12} className="text-[#064E3B]"/> {label}</span>
              ))}
            </div>
          </div>

          {/* INSPIRATION PANEL (desktop side; mobile below) */}
          <aside className="lg:col-span-4 space-y-3" data-testid="inspiration-panel">
            <div className="flex items-center gap-2">
              <div className="w-1.5 h-1.5 rounded-full bg-[#C9A45C]"/>
              <div className="text-[10px] uppercase tracking-[0.22em] text-[#C9A45C] font-semibold">Popular AI Concierge ideas</div>
            </div>
            {INSPIRATIONS.map(({ icon: Icon, title, city: c, prompt }) => (
              <button key={title} type="button" data-testid={`inspiration-${title.toLowerCase().replace(/\s+/g,'-')}`}
                onClick={()=>{ setRequest(prompt); setCity(c); }}
                className="group w-full text-left rounded-2xl bg-white border border-[#071A2D]/10 hover:border-[#C9A45C] hover:shadow-luxury transition-all p-4 flex items-start gap-3">
                <span className="w-9 h-9 rounded-full bg-[#C9A45C]/12 text-[#C9A45C] flex items-center justify-center shrink-0">
                  <Icon size={16}/>
                </span>
                <span className="flex-1">
                  <span className="block text-sm font-semibold text-[#071A2D]">{title}</span>
                  <span className="block text-[11px] uppercase tracking-widest text-[#071A2D]/45 mt-0.5">{c}</span>
                </span>
              </button>
            ))}
          </aside>
        </div>

        {/* RESULTS or EMPTY or LOADING or ERROR */}
        {err && !loading && !plan && (
          <div data-testid="ai-error" className="mt-8 p-5 rounded-2xl bg-[#FF7A45]/10 border border-[#FF7A45]/30 text-[#FF7A45] text-sm flex items-start gap-3">
            <Sparkles size={16} className="mt-0.5 shrink-0"/>
            <div className="flex-1">
              <div className="font-semibold">We couldn&apos;t design that plan.</div>
              <div className="text-[#071A2D]/70 mt-1">{err}</div>
            </div>
            <button onClick={submit} className="text-[#FF7A45] underline shrink-0">Try again</button>
          </div>
        )}
        {loading ? (
          <div ref={out} className="mt-14 fade-up" data-testid="ai-loading-card">
            <div className="rounded-3xl bg-white border border-[#00AEEF]/30 p-8 sm:p-10 shadow-luxury-lg">
              <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.22em] text-[#00AEEF] font-semibold">
                <Sparkles size={12} className="animate-pulse"/> AI Concierge · Designing your experience
              </div>
              <h2 className="font-serif text-2xl sm:text-3xl mt-3 text-[#071A2D]">Crafting your TREL plan…</h2>
              <p className="text-sm text-[#071A2D]/65 mt-2 max-w-xl">
                Our concierge AI is composing the itinerary, matching trusted TREL Concierges, and pricing the add-ons. This usually takes 20–60 seconds.
              </p>
              <div className="mt-6 flex items-center gap-3">
                <div className="w-9 h-9 rounded-full border-[3px] border-[#00AEEF] border-t-transparent animate-spin"/>
                <div className="flex-1 h-1.5 rounded-full bg-[#00AEEF]/15 overflow-hidden">
                  <div className="h-full w-1/3 bg-[#00AEEF] rounded-full animate-pulse"/>
                </div>
              </div>
              <div className="mt-6 grid sm:grid-cols-3 gap-3">
                {["Itinerary", "Providers", "Add-ons"].map(l => (
                  <div key={l} className="p-4 rounded-2xl bg-[#FFF8EE] border border-[#071A2D]/8 animate-pulse">
                    <div className="h-3 w-20 bg-[#064E3B]/15 rounded"/>
                    <div className="h-5 w-32 bg-[#071A2D]/10 rounded mt-2"/>
                  </div>
                ))}
              </div>
            </div>
          </div>
        ) : plan ? (
          <div ref={out} className="mt-14 fade-up">
            <div className="rounded-3xl bg-white border border-[#071A2D]/10 p-7 sm:p-10 shadow-luxury-lg">
              <div className="text-[10px] uppercase tracking-[0.22em] text-[#00AEEF] font-semibold flex items-center gap-2">
                <Sparkles size={12}/> Your TREL Experience Plan
              </div>
              <h2 className="font-serif text-3xl sm:text-4xl mt-3" data-testid="plan-title">{plan.title}</h2>
              <p className="mt-3 text-[#071A2D]/75 max-w-3xl">{plan.summary}</p>

              <div className="mt-8 grid grid-cols-2 sm:grid-cols-3 gap-5">
                <Stat label="Estimated price" value={`$${plan.estimated_price}`}/>
                <Stat label="Duration" value={plan.duration}/>
                <Stat label="Guests" value={String(guests)}/>
              </div>

              <div className="mt-10">
                <h3 className="font-serif text-2xl mb-4">Suggested itinerary</h3>
                <div className="space-y-3">
                  {plan.itinerary?.map((it, i) => (
                    <div key={`${it.time || "step"}-${i}`} data-testid={`itinerary-item-${i}`} className="flex gap-5 p-5 rounded-2xl bg-[#FFF8EE] border border-[#071A2D]/10 hover:border-[#064E3B]/40 transition">
                      <div className="w-20 shrink-0 text-[#FF7A45] font-semibold">{it.time}</div>
                      <div className="flex-1">
                        <div className="font-semibold text-[#071A2D]">{it.title}</div>
                        <div className="text-sm text-[#071A2D]/70 mt-1">{it.description}</div>
                        <div className="text-xs text-[#064E3B]/70 mt-2 flex items-center gap-1"><MapPin size={11}/> {it.location}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="mt-8 grid sm:grid-cols-2 gap-6">
                <div>
                  <div className="text-[10px] uppercase tracking-[0.22em] text-[#064E3B]/70 font-semibold">Recommended providers</div>
                  <div className="mt-2 flex flex-wrap gap-2">
                    {plan.providers_needed?.map(p=><span key={p} className="px-3 py-1 rounded-full bg-[#064E3B] text-[#FFF8EE] text-xs">{p}</span>)}
                  </div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-[0.22em] text-[#064E3B]/70 font-semibold">Suggested add-ons</div>
                  <div className="mt-2 flex flex-wrap gap-2">
                    {plan.addons?.map(a=><span key={a} className="px-3 py-1 rounded-full border border-[#C9A45C] text-[#C9A45C] text-xs">{a}</span>)}
                  </div>
                </div>
              </div>

              {plan.personal_note && (
                <div className="mt-8 p-6 rounded-2xl bg-[#00AEEF]/8 border border-[#00AEEF]/25 italic text-[#071A2D]/85">&ldquo;{plan.personal_note}&rdquo;</div>
              )}

              <div className="mt-8 flex flex-col sm:flex-row gap-3">
                {/* Phase 46 PRIMARY CTA — broadcasts the plan as a TREL
                    Request Brief to eligible verified providers, then takes
                    the customer to the brief detail page where offers arrive.
                    "Find My Concierge" replaces the old "Meet your
                    recommended Concierges" inline scroll. */}
                <button
                  type="button"
                  data-testid="plan-find-concierge"
                  onClick={findMyConcierge}
                  disabled={broadcasting}
                  className="btn-coral text-center inline-flex items-center justify-center gap-2 disabled:opacity-70"
                >
                  {broadcasting ? "Sending to Concierges…" : "Find My Concierge →"}
                </button>
                <button onClick={()=>{ setPlan(null); }} className="btn-outline-emerald" data-testid="plan-customize">Customize</button>
                <button className="px-5 py-3 rounded-full border border-[#C9A45C] text-[#C9A45C] hover:bg-[#C9A45C]/10 transition font-medium" data-testid="plan-save">Save plan</button>
              </div>
              {broadcastErr && (
                <div className="mt-3 text-sm text-rose-700 bg-rose-50 border border-rose-200 px-3 py-2 rounded-lg">
                  {broadcastErr}
                </div>
              )}
            </div>

            {recommended.length > 0 && (
              <div className="mt-10" id="recommended-concierges-section" data-testid="recommended-concierges-section">
                <div className="text-[10px] uppercase tracking-[0.22em] text-[#00AEEF] font-semibold flex items-center gap-2">
                  <Sparkles size={12}/> Trusted Concierges for this experience
                </div>
                <h3 className="font-serif text-2xl sm:text-3xl mt-2">Recommended matches in {city}</h3>
                <p className="text-sm text-[#071A2D]/65 mt-2 max-w-2xl">
                  These TREL Concierges specialize in moments like yours. View their profile or jump straight into planning — they handle every detail.
                </p>
                <div className="mt-6 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
                  {recommended.map((c) => (
                    <RecommendedConciergeCard
                      key={c.user_id}
                      c={c}
                      matchReason={buildMatchReason(c, plan, occasion)}
                    />
                  ))}
                </div>
              </div>
            )}
          </div>
        ) : (
          <div data-testid="ai-empty-state" className="mt-14 text-center max-w-md mx-auto">
            <div className="w-12 h-12 mx-auto rounded-full bg-[#064E3B]/8 flex items-center justify-center">
              <Sparkles size={18} className="text-[#064E3B]"/>
            </div>
            <h3 className="font-serif text-2xl mt-4 text-[#071A2D]">Tell us the moment.</h3>
            <p className="text-sm text-[#071A2D]/60 mt-2">TREL will shape the plan — itinerary, providers, and add-ons curated to your guests, budget, and occasion.</p>
          </div>
        )}
      </div>
    </div>
  );
}

const Field = ({ label, children }) => (
  <div>
    <label className="text-[10px] uppercase tracking-[0.22em] text-[#064E3B]/70 font-semibold">{label}</label>
    <div className="mt-1.5">{children}</div>
  </div>
);

const Stat = ({ label, value }) => (
  <div className="p-4 rounded-2xl bg-[#FFF8EE] border border-[#071A2D]/8">
    <div className="text-[10px] uppercase tracking-[0.22em] text-[#064E3B]/70 font-semibold">{label}</div>
    <div className="font-serif text-2xl mt-1 text-[#071A2D]">{value}</div>
  </div>
);

// Trust scoring for AI-recommended concierges: Elite > Certified+Identity > Identity > rest.
// Used to surface the most credible concierges first in the recommendation grid.
function trustScore(p) {
  let s = 0;
  if (p?.elite_badge_active) s += 8;
  if (p?.trel_certified || (p?.certification && p.certification.completed_at)) s += 4;
  if (p?.identity_verified) s += 2;
  if (p?.trel_plus_active) s += 1;
  s += Math.min(2, (p?.rating || 0) / 5 * 2);
  return s;
}

// Human-readable match reason shown on each Recommended Match card.
function buildMatchReason(c, plan, occasion) {
  const firstName = (c?.name || "this Concierge").split(" ")[0];
  const specs = (c?.specializes || c?.categories || []);
  const occ = (occasion || "").trim();
  if (occ && specs.length) {
    return `${firstName} specializes in ${specs.slice(0, 2).join(" & ").toLowerCase()} — a strong match for ${occ.toLowerCase()} moments like yours.`;
  }
  if (plan?.title && specs.length) {
    return `${firstName} specializes in ${specs.slice(0, 2).join(" & ").toLowerCase()} — well-suited to "${plan.title}".`;
  }
  if (c?.city) {
    return `${firstName} is a trusted Experience Concierge in ${c.city} with experience in moments like yours.`;
  }
  return `${firstName} specializes in creating experiences similar to your request.`;
}
